/**
 * 导演模式工作台（统一入口，参考电商复刻 SPA 结构）
 *
 * 路由：
 *   /director              → 项目库（可能展示选中第一个）
 *   /director/:projectId   → 选中某个项目，右侧显示工作区
 *
 * 布局：
 *   - 顶部固定 navbar（站点 logo + 工作台标题 + 用户信息 + 全部激活/预览/分享 按钮浮于 hero）
 *   - 主体 lg:flex
 *     - 左：DirectorSidebar（项目列表 + 新建按钮 + 进行中/已归档 切换）
 *     - 右：DirectorProjectWorkspace（选中项目的 hero + 故事板 + 检查器）
 *   - 窄屏：sidebar 退化为抽屉
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';
import toast from '../../components/Toast';
import { api as imageApi } from '../../services/api';
import { usePlatformSession } from '../../platform/session';
import { AppShell } from '../../platform/AppShell';
import { getApplicationIcon } from '../applications';
import {
  directorApi,
  DirectorApiError,
  type DirectorProjectWithCover,
} from './services/directorApi';
import { DirectorSidebar } from './components/DirectorSidebar';
import { DirectorProjectWorkspace } from './components/DirectorProjectWorkspace';
import { NewProjectDialog } from './components/NewProjectDialog';
import { EditProjectDialog } from './components/EditProjectDialog';
import { ShareDialog } from './components/share/ShareDialog';
import { PageBlobs } from './components/PageBlobs';

type Filter = 'active' | 'archived';

export function DirectorWorkbench() {
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId?: string }>();

  const { credits, isLoggedIn, refreshUser, setUser } = usePlatformSession();
  // AppShell 的 context 由本组件渲染的 <AppShell> 提供，hooks 执行时尚不存在，
  // 因此登录弹窗走受控 authModal prop（与画布同模式）而非 useAppShell
  const [showAuthModal, setShowAuthModal] = useState(false);
  const openAuthModal = () => setShowAuthModal(true);
  const [showNewDialog, setShowNewDialog] = useState(false);

  const [filter, setFilter] = useState<Filter>('active');
  const [projects, setProjects] = useState<DirectorProjectWithCover[]>([]);
  const [listLoading, setListLoading] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 编辑 / 分享对话框
  const [editingProject, setEditingProject] = useState<DirectorProjectWithCover | null>(null);
  const [sharingProject, setSharingProject] = useState<DirectorProjectWithCover | null>(null);

  /**
   * 工作区重新加载信号：每次 +1 都会让 DirectorProjectWorkspace 重拉项目，
   * 用于"修改项目设置（视频模型 / 图片模型）后实时刷新派生 UI（如分镜可选时长）"。
   */
  const [workspaceReloadSignal, setWorkspaceReloadSignal] = useState(0);

  // 站点名（保留访问，确保接口可用，但侧栏不展示）
  useEffect(() => {
    imageApi.getSiteName().catch(() => undefined);
  }, []);

  // 用户信息
  useEffect(() => {
    if (!isLoggedIn) return;
    void refreshUser();
  }, [isLoggedIn, refreshUser]);

  // 加载项目列表
  const loadProjects = useCallback(async (): Promise<DirectorProjectWithCover[]> => {
    if (!isLoggedIn) return [];
    setListLoading(true);
    try {
      const result = await directorApi.listProjects({ filter, page: 1, pageSize: 100 });
      setProjects(result.data);
      return result.data;
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '加载失败');
      return [];
    } finally {
      setListLoading(false);
    }
  }, [filter, isLoggedIn]);

  useEffect(() => {
    loadProjects().then((list) => {
      // 如果 URL 没有 projectId 且列表非空，跳转到第一个项目
      if (!urlProjectId && list.length > 0) {
        navigate(`/apps/director/${list[0].id}`, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, isLoggedIn]);

  const handleSelectProject = (id: string) => {
    navigate(`/apps/director/${id}`);
    setSidebarOpen(false);
  };

  const handleCreateProject = () => {
    if (!isLoggedIn) {
      openAuthModal();
      return;
    }
    setShowNewDialog(true);
  };

  const handleArchive = async (id: string) => {
    try {
      await directorApi.archiveProject(id);
      toast.success('项目已归档');
      const list = await loadProjects();
      if (urlProjectId === id) {
        const next = list[0]?.id;
        navigate(next ? `/apps/director/${next}` : '/apps/director', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '归档失败');
    }
  };

  const handleUnarchive = async (id: string) => {
    try {
      await directorApi.unarchiveProject(id);
      toast.success('项目已恢复');
      await loadProjects();
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '恢复失败');
    }
  };

  const handleDelete = async (id: string) => {
    let confirmText = '确定要彻底删除这个项目吗？所有内容将无法恢复，已扣的积分不退。';
    try {
      const stats = await directorApi.getProjectDeletePreview(id);
      const items: string[] = [];
      if (stats.shotCount > 0) items.push(`${stats.shotCount} 个分镜`);
      if (stats.keyframeCount > 0) items.push(`${stats.keyframeCount} 张关键帧候选`);
      if (stats.videoCount > 0) items.push(`${stats.videoCount} 条视频任务`);
      if (stats.compositionCount > 0) items.push(`${stats.compositionCount} 个成片`);
      if (stats.sceneCount > 0) items.push(`${stats.sceneCount} 个场景`);
      if (items.length > 0) {
        confirmText = `确定要彻底删除这个项目吗？\n将无法恢复：${items.join('、')}。\n已扣的积分不退。`;
      }
    } catch {
      // 用通用文案
    }
    if (!window.confirm(confirmText)) return;
    try {
      await directorApi.hardDeleteProject(id);
      toast.success('项目已删除');
      const list = await loadProjects();
      if (urlProjectId === id) {
        const next = list[0]?.id;
        navigate(next ? `/apps/director/${next}` : '/apps/director', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    }
  };

  // 项目内变更后由 workspace 触发，刷新侧栏（更新分镜数 / 状态等）
  const handleProjectChanged = useCallback(() => {
    loadProjects();
  }, [loadProjects]);

  const currentProject = projects.find((p) => p.id === urlProjectId) ?? null;

  return (
    <AppShell
      appName="导演工作台"
      appSubtitle="关键帧 → 视频激活 → 合成成片"
      appIcon={(() => { const Icon = getApplicationIcon('director'); return <Icon className="h-5 w-5" />; })()}
      backgroundClassName="bg-[#eef3f8]"
      onOpenSidebar={() => setSidebarOpen(true)}
      authModal={{
        open: showAuthModal,
        onOpen: openAuthModal,
        onClose: () => setShowAuthModal(false),
        onSuccess: (u) => {
          setUser(u);
          setShowAuthModal(false);
        },
      }}
    >
      <PageBlobs />

      <div className="relative lg:flex">
        {/* 左侧 sidebar（lg 起常驻） */}
        <DirectorSidebar
          projects={projects}
          currentProjectId={urlProjectId ?? null}
          loading={listLoading}
          filter={filter}
          onChangeFilter={setFilter}
          onSelectProject={handleSelectProject}
          onCreateProject={handleCreateProject}
          onEdit={(id) => {
            const p = projects.find((x) => x.id === id);
            if (p) setEditingProject(p);
          }}
          onShare={(id) => {
            const p = projects.find((x) => x.id === id);
            if (p) setSharingProject(p);
          }}
          onArchive={handleArchive}
          onUnarchive={handleUnarchive}
          onDelete={handleDelete}
          className="hidden lg:block"
        />

        {/* 窄屏 sidebar 抽屉 */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-[60] lg:hidden">
            <button
              type="button"
              aria-label="关闭项目库"
              className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
              onClick={() => setSidebarOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-[86vw] max-w-[360px] overflow-hidden border-r border-white/70 bg-white shadow-2xl shadow-slate-950/25">
              <DirectorSidebar
                projects={projects}
                currentProjectId={urlProjectId ?? null}
                loading={listLoading}
                filter={filter}
                onChangeFilter={setFilter}
                onSelectProject={(id) => {
                  navigate(`/apps/director/${id}`);
                  setSidebarOpen(false);
                }}
                onCreateProject={() => {
                  handleCreateProject();
                  setSidebarOpen(false);
                }}
                onEdit={(id) => {
                  const p = projects.find((x) => x.id === id);
                  if (p) {
                    setEditingProject(p);
                    setSidebarOpen(false);
                  }
                }}
                onShare={(id) => {
                  const p = projects.find((x) => x.id === id);
                  if (p) {
                    setSharingProject(p);
                    setSidebarOpen(false);
                  }
                }}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onDelete={handleDelete}
              />
            </div>
          </div>
        )}

        {/* 右侧主体 */}
        <main className="min-h-[calc(100vh-64px)] flex-1 lg:h-[calc(100vh-64px)] lg:overflow-y-auto">
          {!isLoggedIn ? (
            <EmptyState
              title="登录后查看你的导演项目"
              description="导演模式让你用图片打草稿、用视频出成片，自动合成完整短剧"
              action={
                <button
                  type="button"
                  onClick={openAuthModal}
                  className="rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800"
                >
                  立即登录
                </button>
              }
            />
          ) : listLoading && projects.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-slate-500" />
            </div>
          ) : !urlProjectId || !currentProject ? (
            <EmptyState
              title={projects.length === 0 ? '还没有项目' : '请选择一个项目'}
              description={
                projects.length === 0
                  ? '点左侧"新建导演项目"开始创作你的第一部短剧'
                  : '从左侧项目库选择一个项目，进入工作区'
              }
              action={
                projects.length === 0 ? (
                  <button
                    type="button"
                    onClick={handleCreateProject}
                    className="rounded-2xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-300/70 transition hover:bg-slate-800"
                  >
                    新建导演项目
                  </button>
                ) : null
              }
            />
          ) : (
            <DirectorProjectWorkspace
              key={urlProjectId}
              projectId={urlProjectId}
              credits={credits}
              onProjectChanged={handleProjectChanged}
              reloadSignal={workspaceReloadSignal}
            />
          )}
        </main>
      </div>

      {showNewDialog && (
        <NewProjectDialog
          open={showNewDialog}
          onClose={() => setShowNewDialog(false)}
          onCreated={(id) => {
            setShowNewDialog(false);
            loadProjects().then(() => {
              navigate(`/apps/director/${id}`);
            });
          }}
        />
      )}

      {editingProject && (
        <EditProjectDialog
          open={!!editingProject}
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={() => {
            setEditingProject(null);
            loadProjects();
            // 触发工作区重新拉项目（视频模型可能改了，分镜可选时长等需要刷新）
            setWorkspaceReloadSignal((v) => v + 1);
          }}
        />
      )}

      {sharingProject && (
        <ShareDialog
          open={!!sharingProject}
          projectId={sharingProject.id}
          hasCompletedComposition={!!sharingProject.lastCompositionId}
          onClose={() => setSharingProject(null)}
        />
      )}
    </AppShell>
  );
}

function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex h-full items-center justify-center px-6 py-12">
      <div className="max-w-md rounded-[28px] border border-white/70 bg-white/85 p-10 text-center shadow-[0_24px_80px_rgba(15,23,42,0.08)] backdrop-blur-xl">
        <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-lg shadow-sky-200/70">
          <Sparkles className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
        {action && <div className="mt-6">{action}</div>}
      </div>
    </div>
  );
}
